import { COLUMN_META, type ColumnMeta } from "./db/columnMeta.generated";

// @ds/config/presentationConfig — Layer B of docs/plans/schema-codegen-architecture.md.
//
// Layer A (`*.fields.generated.ts` + `definePresentation`) answers "what is each
// field" (binding → widget/display/enum). Layer B answers "where does it go":
// list column placement (width / pinned / defaultVisible / order), view section
// grouping, and which fields the form edits.
//
// Placement is PURE DATA (no component refs), so the generated config file is
// fully expanded + inspectable (`<entity>.config.generated.ts`). This module
// owns only the TYPES and the MERGE — the defaults are emitted by the codegen
// (`scripts/gen-entity.mjs`); per-entity tweaks live in a thin override that
// calls `definePresentationConfig(generated, overrides)`.

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ColumnPlacement {
  /** Field key (matches a key in the presentation schema). */
  key: string;
  /** Pixel width; omitted → auto. */
  width?: number;
  /** Frozen column. */
  pinned?: "left" | "right";
  /** Shown by default (vs. available behind the column picker). */
  defaultVisible?: boolean;
  /** Cell text alignment. */
  align?: "left" | "center" | "right";
  /** Can the column be toggled off via the picker. Default true. */
  hideable?: boolean;
  /** Show a clickable sort affordance on the column header (server-side sort). */
  sorter?: boolean;
}

export interface ViewSection {
  title: string;
  /** Field keys rendered in this section, in order. */
  fields: string[];
}

export interface PresentationConfig {
  list: { columns: ColumnPlacement[] };
  view: { sections: ViewSection[] };
  form: { fields: string[] };
}

// ─── Overrides ──────────────────────────────────────────────────────────────

export interface PresentationConfigOverrides {
  list?: {
    /** Synthetic columns not present in the generated set (e.g. a derived
     *  `status` field). Added to the column pool before ordering. */
    add?: ColumnPlacement[];
    /** Per-key placement overrides (shallow-merged onto the generated column). */
    columns?: Record<string, Partial<Omit<ColumnPlacement, "key">>>;
    /** Explicit column order by key; unlisted keep generated order after these. */
    order?: string[];
    /** Keys to drop from the list entirely. */
    hide?: string[];
  };
  /** Replaces the generated view sections wholesale when present. */
  view?: { sections?: ViewSection[] };
  /** Replaces the generated form field list wholesale when present. */
  form?: { fields?: string[] };
}

// ─── Default placement — derived at runtime from COLUMN_META ────────────────

const AUDIT = new Set(["created_at", "updated_at"]);
const INTERNAL = new Set(["is_deleted"]);

/** Default list-column placement for a column, by Postgres type / role. */
function placement(col: string, meta: ColumnMeta): ColumnPlacement {
  if (col === "id") return { key: col, width: 70, defaultVisible: false };
  if (meta.fk) return { key: col, width: 200, defaultVisible: true };
  if (AUDIT.has(col)) return { key: col, width: 160, defaultVisible: false };
  if (meta.dt === "date") return { key: col, width: 120, defaultVisible: true };
  if (meta.dt === "boolean") return { key: col, width: 90, defaultVisible: true };
  if (["numeric", "bigint", "integer", "smallint", "double precision", "real"].includes(meta.dt))
    return { key: col, width: 120, defaultVisible: true };
  return { key: col, defaultVisible: true }; // text/enum/other → auto width
}

/** The generated default `PresentationConfig` for a table, from `COLUMN_META`. */
export function defaultPlacement(table: string): PresentationConfig {
  const cols = COLUMN_META[table] ?? {};
  const entries = Object.entries(cols).filter(([, m]) => m.dt !== "tsvector");
  const listColumns = entries.filter(([c]) => !INTERNAL.has(c)).map(([c, m]) => placement(c, m));
  const detailFields = entries.filter(([c]) => !INTERNAL.has(c) && !AUDIT.has(c)).map(([c]) => c);
  const auditFields = entries.filter(([c]) => AUDIT.has(c)).map(([c]) => c);
  const formFields = entries
    .filter(([c]) => c !== "id" && !INTERNAL.has(c) && !AUDIT.has(c))
    .map(([c]) => c);
  return {
    list: { columns: listColumns },
    view: {
      sections: [
        { title: "Szczegóły", fields: detailFields },
        ...(auditFields.length ? [{ title: "Metadane", fields: auditFields }] : []),
      ],
    },
    form: { fields: formFields },
  };
}

// ─── Merge ──────────────────────────────────────────────────────────────────

/**
 * Build a `PresentationConfig` for `table` — default placement derived at
 * runtime from `COLUMN_META`, merged with per-entity overrides:
 *   - list columns: per-key shallow merge, optional add/reorder/hide
 *   - view sections / form fields: wholesale replace when the override is present
 */
export function definePresentationConfig(
  table: string,
  overrides: PresentationConfigOverrides = {},
): PresentationConfig {
  const generated = defaultPlacement(table);
  // ── list ──
  const hide = new Set(overrides.list?.hide ?? []);
  const perKey = overrides.list?.columns ?? {};
  let columns = [...generated.list.columns, ...(overrides.list?.add ?? [])]
    .filter((c) => !hide.has(c.key))
    .map((c) => ({ ...c, ...perKey[c.key] }));

  const order = overrides.list?.order;
  if (order) {
    const byKey = new Map(columns.map((c) => [c.key, c]));
    const ordered = order.map((k) => byKey.get(k)).filter((c): c is ColumnPlacement => !!c);
    const rest = columns.filter((c) => !order.includes(c.key));
    columns = [...ordered, ...rest];
  }

  return {
    list: { columns },
    view: { sections: overrides.view?.sections ?? generated.view.sections },
    form: { fields: overrides.form?.fields ?? generated.form.fields },
  };
}
